import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { type LalSegmentApi, lalSegmentStatus } from '@openx/types/segments';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LalReachBarGauge } from '../components/LalReachBarGauge/LalReachBarGauge';
import { LalSegmentReachTable } from '../components/LalSegmentReachTable/LalSegmentReachTable';

type Props = {
  lalSegment: LalSegmentApi;
};

export const LalSegmentReachSection = ({ lalSegment }: Props) => {
  const { t } = useTranslation();

  const {
    status,
    error,
    lookalike_segment_seed_aggregate: seedAggregate,
    lookalike_segment_similarities: segmentSimilarities = [],
  } = lalSegment;

  const errorMessage = error?.message;
  const isProcessing = status === lalSegmentStatus.PROCESSING;
  const hasReachSectionData = segmentSimilarities?.length && seedAggregate;

  const content = useMemo(() => {
    if (!hasReachSectionData) {
      return null;
    }

    return (
      <>
        <LalReachBarGauge segmentSimilarities={segmentSimilarities} baseReach={seedAggregate.segment.reach ?? 0} />
        <LalSegmentReachTable segmentSimilarities={segmentSimilarities} seedAggregate={seedAggregate} />
      </>
    );
  }, [hasReachSectionData, segmentSimilarities, seedAggregate]);

  if (isProcessing && !hasReachSectionData && !errorMessage) {
    return null;
  }

  return (
    <FormSection divider title={t('LAL Segment Reach')} data-test="lal-segment-reach-details">
      <Grid size={6} sx={{ minWidth: 555 }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography>{errorMessage}</Typography>
          </Alert>
        )}
        {content}
      </Grid>
    </FormSection>
  );
};
